import CartSheet from '@/components/CartSheet'
import { useClearCartAfterCountryChange } from '@/components/Checkout/_helpers/hooks/useClearCartAfterCountryChange'
import HydrationZustand from '@/components/Helpers/HydrationZustand'
import { ModalManager } from '@/components/ModalManager'
import { ErrorPage } from '@/components/_ui/ErrorPage'
import LoadingOverlay from '@/components/_ui/LoadingOverlay'
import { Toaster } from '@/components/_ui/Sonner'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useInitGTMParameters } from '@/lib/hooks/google-analytics/useInitGtmParameters'
import { useCloseMenusOnPageChange } from '@/lib/hooks/helpers/useCloseMenusOnPageChange'
import { useGetOverwriteTerms } from '@/lib/hooks/helpers/useGetOverwriteTerms'
import { useInitApp } from '@/lib/hooks/initializers/useInitApp'
import { useReAuthenticateUser } from '@/lib/hooks/initializers/useReAuthenticateUser'
import { useRefreshLoginState } from '@/lib/hooks/services/useRefreshLoginState'
import useGlobalStore from '@/store/useGlobalStore'
import '@/styles/global.css'
import { GoogleTagManager } from '@next/third-parties/google'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import type { AppProps } from 'next/app'
import NextNProgress from 'nextjs-progressbar'
import { useEffect } from 'react'
import type { FallbackProps } from 'react-error-boundary'
import { ErrorBoundary } from 'react-error-boundary'
import { useInitialReferrer } from '@/lib/hooks/mixpanel/useInitialReferrer'
import { useMixpanelProperties } from '@/lib/hooks/mixpanel/useMixpanelProperties'
import initializeDatadog from '@/lib/services/datadog/initializeDatadog'
import { initializeLoggingService } from '@/lib/services/datadog/datadogLoggingService'
import { useOldUserInitialize } from '@/lib/hooks/mixpanel/useOldUserInitialize'
import '@/lib/polyfills'

function fallbackRender({ error }: FallbackProps) {
  // Not ideal to use the as keyword, but the library does not return a type for this
  return <ErrorPage error={error as Error} />
}

function App({ Component, pageProps }: AppProps) {
  useInitApp()
  useReAuthenticateUser()
  useRefreshLoginState()
  useCloseMenusOnPageChange()
  useClearCartAfterCountryChange()
  useGetOverwriteTerms()
  useInitialReferrer()
  useMixpanelProperties()
  useOldUserInitialize()
  const { gtmId } = useGTM()

  useInitGTMParameters()

  useEffect(() => {
    initializeDatadog()
    initializeLoggingService()
  }, [])

  useEffect(() => {
    // TODO: improve upon this to only reload when there's cart changes
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload()
      }
    })
  }, [])

  return (
    <HydrationZustand>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      />
      <ModalManager>
        <GoogleTagManager gtmId={gtmId} />
        <CartSheet />
        <LoadingOverlay />
        <ErrorBoundary fallbackRender={fallbackRender}>
          <Component {...pageProps} />
        </ErrorBoundary>
        <NextNProgress color="#6794c5" height={5} showOnShallow={false} />
        <Toaster richColors />
      </ModalManager>
    </HydrationZustand>
  )
}

export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID ?? '',
  context: {
    kind: 'country',
    key: useGlobalStore.getState().app.alpha2,
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
})(App)
